import { useEffect, useSyncExternalStore } from 'react';
import { useTour } from '@reactour/tour';
import { getActiveTourKey } from './activeTour';
import { REGISTER_PLATE_STEP } from './tourRegistry';
import {
	subscribeRegisterPlateTour,
	getRegisterPlateTourSnapshot,
} from './registerPlateTourState';

const TOUR_KEY = 'canpark_register_plate';
const REGISTER_BTN_SELECTOR = '[data-tour="register-plates-button"]';

// Action-driven gates for the CANpark "Register a plate" tour. Runs inside
// CANparkAdmin — i.e. only once we're on /canpark-admin. The registration modal
// lives deep inside a per-lot card, so it reports its state through the
// registerPlateTourState store rather than props. Gates:
//   MANAGE_SITES_ITEM -> REGISTER_BUTTON once a lot's "Register Plates" button renders
//   REGISTER_BUTTON    -> PLATE          the adding form opened (modal + adding stage)
//   SAVE               -> FINISH         the sandboxed save ran
// PLATE -> ADD_MORE -> VALIDITY -> SAVE are Next-driven (PLATE/VALIDITY hold Next
// via tourGate). The sidebar menu steps are handled by useSidebarTourControl.
export const useRegisterPlateTourSync = () => {
	const { isOpen, currentStep, setCurrentStep } = useTour();
	const { formOpen, saved } = useSyncExternalStore(
		subscribeRegisterPlateTour,
		getRegisterPlateTourSnapshot,
	);

	useEffect(() => {
		if (!isOpen || getActiveTourKey() !== TOUR_KEY) return;

		if (currentStep === REGISTER_PLATE_STEP.MANAGE_SITES_ITEM) {
			// Lots load asynchronously, so wait for the first card's Register Plates
			// button to exist before advancing — otherwise the REGISTER_BUTTON step
			// activates with no anchor and its popover never shows.
			const found = () => !!document.querySelector(REGISTER_BTN_SELECTOR);
			if (found()) {
				setCurrentStep(REGISTER_PLATE_STEP.REGISTER_BUTTON);
				return;
			}
			const observer = new MutationObserver(() => {
				if (found()) {
					observer.disconnect();
					setCurrentStep(REGISTER_PLATE_STEP.REGISTER_BUTTON);
				}
			});
			observer.observe(document.body, { childList: true, subtree: true });
			return () => observer.disconnect();
		}

		if (currentStep === REGISTER_PLATE_STEP.REGISTER_BUTTON && formOpen) {
			setCurrentStep(REGISTER_PLATE_STEP.PLATE);
		} else if (currentStep === REGISTER_PLATE_STEP.SAVE && saved) {
			setCurrentStep(REGISTER_PLATE_STEP.FINISH);
		}
	}, [isOpen, currentStep, formOpen, saved, setCurrentStep]);
};
